import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './services/supabase';
import { Loader2, Monitor } from 'lucide-react';
import type { AppView } from './types';
import { SHOWCASE_VIDEO, APP_TAGLINE } from './constants';

import FluidBackground from './components/FluidBackground';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PublicShowcase from './components/PublicShowcase';

function getPublicSlug(): string | null {
  const match = window.location.pathname.match(/^\/view\/(.+)$/);
  if (!match) return null;
  return match[1].replace(/\/+$/, '').trim().split('/')[0]?.trim() ?? null;
}

const PENDING_OPEN_KEY = 'showcase.pendingOpen';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Remembers `?open=<showcase id>` (e.g. from Photoplan) until sign-in completes, and drops it from the URL. */
function capturePendingOpen() {
  const url = new URL(window.location.href);
  const id = url.searchParams.get('open');
  if (id === null) return;
  url.searchParams.delete('open');
  window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
  if (UUID_PATTERN.test(id)) {
    try {
      sessionStorage.setItem(PENDING_OPEN_KEY, id);
    } catch {
      // Opening the showcase directly is optional.
    }
  }
}

function takePendingOpen(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_OPEN_KEY);
    if (id) sessionStorage.removeItem(PENDING_OPEN_KEY);
    return id;
  } catch {
    return null;
  }
}

interface AppRoute {
  view: AppView;
  showcaseId: string | null;
}

function parseRoute(pathname: string): AppRoute | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/') return { view: 'DASHBOARD', showcaseId: null };
  if (path === '/new') return { view: 'CREATE', showcaseId: null };
  if (path === '/agents') return { view: 'AGENTS', showcaseId: null };
  const match = path.match(/^\/projects\/([^/]+)(?:\/(leads|analytics))?$/);
  if (!match || !UUID_PATTERN.test(match[1])) return null;
  const view: AppView = match[2] === 'leads' ? 'LEADS' : match[2] === 'analytics' ? 'ANALYTICS' : 'EDITOR';
  return { view, showcaseId: match[1] };
}

function pathForRoute(view: AppView, showcaseId: string | null): string | null {
  switch (view) {
    case 'DASHBOARD': return '/';
    case 'CREATE': return '/new';
    case 'AGENTS': return '/agents';
    case 'EDITOR': return showcaseId ? `/projects/${showcaseId}` : null;
    case 'LEADS': return showcaseId ? `/projects/${showcaseId}/leads` : null;
    case 'ANALYTICS': return showcaseId ? `/projects/${showcaseId}/analytics` : null;
    default: return null;
  }
}

const RETURN_PATH_KEY = 'showcase.returnPath';

/** Keeps a deep link (e.g. /projects/<id>) across sign-in, including OAuth and magic-link redirects. */
function rememberReturnPath() {
  const route = parseRoute(window.location.pathname);
  if (!route || route.view === 'DASHBOARD') return;
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname);
  } catch {
    // Returning to the deep link is optional.
  }
}

function takeReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_KEY);
    if (path) sessionStorage.removeItem(RETURN_PATH_KEY);
    return path;
  } catch {
    return null;
  }
}

function LoginLanding({ onAuthSuccess }: { onAuthSuccess: () => void }) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
      <FluidBackground />
      <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
        <Header
          user={null}
          credits={0}
          onSignOut={() => {}}
          onNavigate={() => {}}
          currentView="LOADING"
          onLogin={() => { setAuthMode('login'); setShowLoginModal(true); }}
          onSignUp={() => { setAuthMode('signup'); setShowLoginModal(true); }}
        />
        <main className="flex-1">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-900/30 backdrop-blur-sm border border-cyan-500/20 text-cyan-300 rounded-full text-sm font-medium mb-8">
                <Monitor size={14} className="shrink-0" />
                Property Presentation Builder
              </div>

              <div className="flex justify-center mb-6">
                <img
                  src="/Logo.png"
                  alt="Showcase by ReHub"
                  className="h-24 md:h-28 w-auto object-contain drop-shadow-[0_0_24px_rgba(255,255,255,0.12)]"
                />
              </div>

              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                {APP_TAGLINE}
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => { setAuthMode('signup'); setShowLoginModal(true); }}
                  className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-500 hover:to-teal-500 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-cyan-900/40 hover:shadow-cyan-500/30 hover:scale-105 border border-cyan-500/30"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-700 group-hover:rotate-[360deg] transition-transform duration-700">
                    <Monitor size={20} strokeWidth={2} />
                  </span>
                  Try Showcase Free
                </button>
                <button
                  onClick={() => { setAuthMode('login'); setShowLoginModal(true); }}
                  className="px-8 py-5 bg-slate-900/80 backdrop-blur-sm border-2 border-slate-500 hover:border-slate-400 text-slate-200 hover:text-white rounded-2xl font-semibold text-lg transition-all shadow-lg"
                >
                  Sign In
                </button>
              </div>

              <div className="mt-12 w-full max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-700/80 shadow-2xl shadow-cyan-900/20">
                <div className="relative w-full pt-[56.25%] bg-black">
                  <iframe
                    src={SHOWCASE_VIDEO.embedUrl}
                    title={SHOWCASE_VIDEO.title}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
        <div className="shrink-0">
          <Footer />
        </div>
        {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-lg">
              <Login
                onAuthSuccess={() => { setShowLoginModal(false); onAuthSuccess(); }}
                onClose={() => setShowLoginModal(false)}
                initialMode={authMode}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const publicSlug = getPublicSlug();
  if (publicSlug) {
    return <PublicShowcase slug={publicSlug} />;
  }

  const [view, setView] = useState<AppView>('LOADING');
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [selectedShowcaseId, setSelectedShowcaseId] = useState<string | null>(null);
  const [userCredits, setUserCredits] = useState(0);
  const intentionalSignOutRef = useRef(false);

  const loadCredits = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('credits')
        .eq('id', userId)
        .single();
      if (data) setUserCredits(data.credits ?? 0);
    } catch (err) {
      console.warn('Failed to fetch credits:', err);
    }
  }, []);

  const viewRef = useRef<AppView>(view);
  viewRef.current = view;
  const selectedShowcaseIdRef = useRef<string | null>(selectedShowcaseId);
  selectedShowcaseIdRef.current = selectedShowcaseId;

  const navigate = useCallback((target: AppView, showcaseId?: string) => {
    if (showcaseId) setSelectedShowcaseId(showcaseId);
    setView(target);
    const path = pathForRoute(target, showcaseId ?? selectedShowcaseIdRef.current);
    if (path && path !== window.location.pathname) {
      window.history.pushState(null, document.title, path);
    }
  }, []);

  /** Opens the signed-in app at whatever the URL points to. */
  const enterApp = useCallback(() => {
    const returnPath = takeReturnPath();
    if (returnPath) window.history.replaceState(null, document.title, returnPath);
    const route = parseRoute(window.location.pathname);
    if (!route) window.history.replaceState(null, document.title, '/');
    const target = route ?? { view: 'DASHBOARD' as AppView, showcaseId: null };
    if (target.showcaseId) setSelectedShowcaseId(target.showcaseId);
    setView(target.view);
  }, []);

  const showLogin = useCallback(() => {
    rememberReturnPath();
    setView('LOGIN');
  }, []);

  useEffect(() => {
    capturePendingOpen();
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (viewRef.current === 'LOADING' || viewRef.current === 'LOGIN') return;
      const route = parseRoute(window.location.pathname) ?? { view: 'DASHBOARD' as AppView, showcaseId: null };
      if (route.showcaseId) setSelectedShowcaseId(route.showcaseId);
      setView(route.view);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (!user || view !== 'DASHBOARD') return;
    const pendingId = takePendingOpen();
    if (pendingId) navigate('EDITOR', pendingId);
  }, [user, view, navigate]);

  useEffect(() => {
    let cancelled = false;
    let callbackTimer: ReturnType<typeof setTimeout> | null = null;
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;

    const initAuth = async () => {
      // 1. SSO tokens from cross-app navigation
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const ssoAccessToken = hashParams.get('sso_access_token');
      const ssoRefreshToken = hashParams.get('sso_refresh_token');

      if (ssoAccessToken && ssoRefreshToken) {
        window.history.replaceState({}, document.title, window.location.pathname);
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: ssoAccessToken,
            refresh_token: ssoRefreshToken,
          });
          if (data.session && !cancelled) {
            setSession(data.session);
            setUser(data.session.user);
            loadCredits(data.session.user.id);
            enterApp();
            return;
          }
          if (error) console.warn('SSO session failed:', error.message);
        } catch (err) {
          console.warn('SSO session error:', err);
        }
      }

      // 2. Auth callback — return early, let onAuthStateChange handle PKCE
      const isAuthCallback = window.location.pathname === '/auth/callback';
      if (isAuthCallback) {
        callbackTimer = setTimeout(() => {
          if (!cancelled) {
            console.warn('Auth callback timeout');
            window.history.replaceState({}, document.title, '/');
            setView('LOGIN');
          }
        }, 8000);
        return;
      }

      // 3. Check existing session
      try {
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        if (cancelled) return;

        if (!existingSession) {
          showLogin();
          return;
        }

        const { data: { user: validatedUser }, error: userError } = await supabase.auth.getUser();
        if (cancelled) return;

        if (validatedUser && !userError) {
          setSession(existingSession);
          setUser(validatedUser);
          loadCredits(validatedUser.id);
          enterApp();
        } else {
          const { data: refreshData } = await supabase.auth.refreshSession();
          if (cancelled) return;

          if (refreshData?.session) {
            setSession(refreshData.session);
            setUser(refreshData.session.user);
            loadCredits(refreshData.session.user.id);
            enterApp();
          } else {
            showLogin();
          }
        }
      } catch {
        if (!cancelled) showLogin();
      }
    };

    initAuth();

    // 4. Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (cancelled) return;

        switch (event) {
          case 'SIGNED_IN':
            setSession(newSession);
            setUser(newSession?.user ?? null);
            if (window.location.pathname === '/auth/callback') {
              window.history.replaceState({}, document.title, '/');
            }
            if (callbackTimer) { clearTimeout(callbackTimer); callbackTimer = null; }
            if (newSession) loadCredits(newSession.user.id);
            if (viewRef.current === 'LOADING' || viewRef.current === 'LOGIN') enterApp();
            break;

          case 'TOKEN_REFRESHED':
            setSession(newSession);
            setUser(newSession?.user ?? null);
            if (newSession) loadCredits(newSession.user.id);
            break;

          case 'SIGNED_OUT':
            if (intentionalSignOutRef.current) {
              intentionalSignOutRef.current = false;
              setSession(null);
              setUser(null);
              setView('LOGIN');
              break;
            }
            if (recoveryTimer) clearTimeout(recoveryTimer);
            recoveryTimer = setTimeout(async () => {
              for (let attempt = 1; attempt <= 8; attempt++) {
                if (cancelled) return;
                try {
                  const { data: { session: recovered } } = await supabase.auth.getSession();
                  if (recovered?.access_token) {
                    const { data: { user: ok } } = await supabase.auth.getUser();
                    if (ok) {
                      setSession(recovered);
                      setUser(recovered.user);
                      loadCredits(recovered.user.id);
                      return;
                    }
                  }
                  const { data: { session: refreshed } } = await supabase.auth.refreshSession();
                  if (refreshed) {
                    setSession(refreshed);
                    setUser(refreshed.user);
                    loadCredits(refreshed.user.id);
                    return;
                  }
                } catch { /* retry */ }
                await new Promise((r) => setTimeout(r, 1500));
              }
              if (cancelled) return;
              setSession(null);
              setUser(null);
              setView('LOGIN');
            }, 500);
            break;

          case 'INITIAL_SESSION':
            if (newSession) {
              setSession(newSession);
              setUser(newSession.user);
            }
            break;
        }
      }
    );

    return () => {
      cancelled = true;
      if (callbackTimer) clearTimeout(callbackTimer);
      if (recoveryTimer) clearTimeout(recoveryTimer);
      subscription.unsubscribe();
    };
  }, [loadCredits, enterApp, showLogin]);

  const handleSignOut = async () => {
    intentionalSignOutRef.current = true;
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    window.history.replaceState(null, document.title, '/');
    setView('LOGIN');
  };

  if (view === 'LOADING') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <FluidBackground />
        <Loader2 className="w-8 h-8 text-cyan-500 animate-spin relative z-10" />
      </div>
    );
  }

  if (view === 'LOGIN') {
    return <LoginLanding onAuthSuccess={enterApp} />;
  }

  const CreateWizard = React.lazy(() => import('./components/CreateWizard'));
  const Editor = React.lazy(() => import('./components/Editor'));
  const LeadsInbox = React.lazy(() => import('./components/LeadsInbox'));
  const AnalyticsView = React.lazy(() => import('./components/AnalyticsView'));
  const AgentPanel = React.lazy(() => import('./components/AgentPanel'));

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <FluidBackground />
      <Header
        user={user}
        credits={userCredits}
        onSignOut={handleSignOut}
        onNavigate={navigate}
        currentView={view}
      />

      <main className="flex-1 relative z-10">
        <React.Suspense fallback={
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
          </div>
        }>
          {view === 'DASHBOARD' && (
            <Dashboard
              user={user!}
              onNavigate={navigate}
            />
          )}
          {view === 'CREATE' && (
            <CreateWizard
              user={user!}
              onNavigate={navigate}
              onComplete={(id: string) => navigate('EDITOR', id)}
            />
          )}
          {view === 'EDITOR' && selectedShowcaseId && (
            <Editor
              showcaseId={selectedShowcaseId}
              user={user!}
              onNavigate={navigate}
              onCreditsChanged={() => user && loadCredits(user.id)}
            />
          )}
          {view === 'LEADS' && selectedShowcaseId && (
            <LeadsInbox
              showcaseId={selectedShowcaseId}
              onNavigate={navigate}
            />
          )}
          {view === 'ANALYTICS' && selectedShowcaseId && (
            <AnalyticsView
              showcaseId={selectedShowcaseId}
              onNavigate={navigate}
            />
          )}
          {view === 'AGENTS' && (
            <AgentPanel
              user={user!}
              onNavigate={navigate}
            />
          )}
        </React.Suspense>
      </main>

      <Footer />
    </div>
  );
}
